<template>
  <div class="reset-password page-content">
    <div class="wrapper wrapper-full-page">
      <div class="reset-page">
        <div class="content">
          <div class="container">
            <div class="row">
              <div class="col-md-8 offset-md-2">
                <div class="header-text">
                  <h2>
                    {{ formTitle }}
                  </h2>
                  <h4>
                    {{ formDescription }}
                  </h4>
                  <hr>
                </div>
              </div>

              <div class="col-md-8 offset-md-2">
                <form
                  v-if="!noEmail"
                  v-loading="status.isLoading"
                  method="post"
                  class="form-horizontal"
                  autocomplete="off"
                  element-loading-custom-class="custom-spinner-class"
                  :element-loading-text="status.loadingMessage"
                  @submit.prevent="submitForm"
                >
                  <div
                    v-if="token"
                    class="update-password"
                  >
                    <password-fields-container
                      v-model:password="password"
                      v-model:confirm-password="confirmPassword"
                      field-class="col-sm-7"
                      @update-validation="updatePasswordValidation"
                    />
                  </div>
                  <div
                    v-else
                    class="reset-request-form"
                  >
                    <div class="row align-items-center">
                      <label class="col-sm-3 control-label"> Email* </label>
                      <div class="col-sm-7">
                        <sanitized-input
                          v-model="email"
                          type="email"
                          :pattern="emailPattern"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <message-html :result-html="status.resultHtml" />

                  <div class="form-submit-button-group">
                    <p-button
                      type="purple"
                      round
                      native-type="submit"
                      :disabled="isSubmitBtnDisabled"
                    >
                      {{ token ? 'Update Password' : 'Send Email' }}
                    </p-button>

                    <p-button
                      v-if="token"
                      type="yellow"
                      round
                      @click="resendEmail"
                    >
                      Resend Email
                    </p-button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import delay from 'yoctodelay';

import util from '@/Utils/util';
import { sendPost } from '@/Utils/api';
import { emailPattern } from '@/constants/app';
import { userService } from '@/services/user';

export default {
  name: 'ResetPassword',

  setup: () => {
    const route = useRoute();
    const router = useRouter();

    const noEmail = ref(false);
    const email = ref('');
    const token = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const status = reactive({
      isLoading: false,
      resultHtml: '',
      loadingMessage: '',
      isInvite: false,
      isPasswordValid: false,
    });

    const isSubmitBtnDisabled = computed(() => {
      if (!token.value) {
        return false;
      }

      return !status.isPasswordValid;
    });

    const formTitle = computed(() => {
      if (token.value) {
        return 'Reset Password';
      }

      if (status.isInvite) {
        return 'Create Password';
      }

      return '';
    });

    const formDescription = computed(() => {
      if (noEmail.value) {
        return 'Call us at <a href="tel:+18554646872">+1 (855) 464-6872</a> to set up your dashboard account.';
      }

      if (token.value) {
        return 'Please enter a password that you want to use for signing in to the GoGoGrandparent dashboard.';
      }

      return `
        Enter your email address below and we will send you password
        ${status.isInvite ? 'create' : 'reset'} instructions.
      `;
    });

    const isPasswordNotMatched = computed(() => {
      if (!password.value) {
        return false;
      }

      return password.value !== confirmPassword.value;
    });

    const updatePasswordValidation = (value: boolean) => {
      status.isPasswordValid = value;
    };

    const submitForm = async () => {
      status.resultHtml = '';
      status.isLoading = true;
      if (token.value) {
        await updatePassword();
      } else {
        await sendForgotEmail();
      }
      status.isLoading = false;
    };

    const updatePassword = async () => {
      status.loadingMessage = 'Updating Password...';
      try {
        const response = await sendPost(`reset/${token.value}`, {
          password: password.value,
          confirm: confirmPassword.value,
        });
        status.loadingMessage = `
          Password is set successfully.
          You will be redirected to the dashboard in a few seconds...
        `;

        await delay(3000);

        email.value = response.data.email;
        login();
      } catch (error) {
        status.resultHtml = util.getErrorResponse(error);
      }
    };

    const sendForgotEmail = async () => {
      status.loadingMessage = 'Sending E-mail...';
      try {
        await sendPost('forgot', {
          email: email.value,
        });

        status.resultHtml = util.setSuccessMessage('Please check your email. We just sent reset instructions.');
      } catch (error) {
        status.resultHtml = util.getErrorResponse(error);
      }
    };

    const login = async () => {
      try {
        const response = await sendPost('login', {
          email: email.value,
          password: password.value,
        });

        const user = response.data.user;
        localStorage.setItem('access_token', response.data.token);
        localStorage.setItem('roles', JSON.stringify(user.roles));

        // this.$ga.set({ userId: user.id })
        window.location.href = '/operator';
      } catch (error) {
        status.resultHtml = util.getErrorResponse(error);
      }
    };

    const resendEmail = () => {
      router.push('/reset');
    };

    const resetData = () => {
      status.resultHtml = '';
      noEmail.value = false;
      token.value = (route.params?.token as string) || '';
      email.value = (route.query?.email as string) || '';
      status.isInvite = route.query?.isInvite ? true : false;

      checkToken()
    };

    const checkToken = async () => {
      if (!token.value) {
        return;
      }

      status.isLoading = true;

      try {
        const response = await userService.checkResetPasswordToken({ token: token.value });

        if (!response?.hasEmail ) {
          noEmail.value = true;
          return;
        }

        if (!response?.expired) {
          return;
        }

        if (response?.hasEmail && !response?.hasPassword) {
          router.push('/reset');
          return;
        }

        if (response?.hasEmail && response?.hasPassword) {
          router.push('/login');
          return;
        }
      } catch (error) {
        status.resultHtml = util.getErrorResponse(error);
      } finally {
        status.isLoading = false;
      }
    }

    watch(route, resetData, {
      immediate: true,
      deep: true,
    });

    return {
      emailPattern,
      email,
      noEmail,
      token,
      password,
      confirmPassword,
      status,
      formTitle,
      formDescription,
      isPasswordNotMatched,
      isSubmitBtnDisabled,

      updatePasswordValidation,
      submitForm,
      resendEmail,
    };
  },
};
</script>

<style lang="scss">
.reset-password {
  &.page-content {
    margin: 100px auto 0;
  }
  h2 {
    margin-bottom: 20px;
  }
  h4 {
    font-size: 20px;
    margin-top: 0;
  }
}
</style>
